import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  useTranslation,
  Button,
  Icon,
  SearchBox,
  Tooltip,
  SingleHierarchySelector,
  SingleSearchSelector,
  Option,
  Spinner,
} from "@familyzone/component-library";

import PropTypes from "prop-types";
import AuthorizationWrapper from "../../components/AuthorizationWrapper";
import { AllOptions, lockedOptions, Signature } from "./types";
import { convertSignature } from "./SignatureSelector";

export interface PolicyLayoutProps {
  disabled?: boolean;
  loading?: boolean;
  showCategorySearch?: boolean;
  showTestPolicy?: boolean;
  showObjectLists?: boolean;
  showFilter?: boolean;
  onChangeLocked?: (selected: Option | null) => void;
  onClickCreatePolicy?: (e: React.SyntheticEvent) => void;
  onClickTestPolicy?: (e: React.SyntheticEvent) => void;
  onClickURLSearch?: (e: React.SyntheticEvent) => void;
  onChangeSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch?: (e: React.SyntheticEvent) => void;
  onChangeSignatureSearch?: (selected: AllOptions | null) => void;
  search?: string;
  signatures?: Signature[];
}

const PolicyLayout: React.FC<PolicyLayoutProps> = (
  {
    disabled = false,
    loading = false,
    showCategorySearch = false,
    showTestPolicy = false,
    showObjectLists = false,
    showFilter = false,
    onChangeLocked = () => "",
    onClickCreatePolicy = () => "",
    onClickTestPolicy = () => "",
    onClickURLSearch = () => "",
    onChangeSearch = () => "",
    onChangeSignatureSearch = () => "",
    onClearSearch = () => "",
    search = "",
    children,
    signatures,
  },
  context
) => {
  const { t } = useTranslation();

  const createPolicyText = t("Create Policy");
  const searchText = t("Category Search");
  const testPolicyText = t("Test Policy");
  const searchBoxText = t("Search by Policy Name");
  const objectLists = t("Object Pools");
  const [selectedSignature, setSelectedSignature] = useState<AllOptions | null>(null);
  const [locked, setLocked] = useState<Option | null>(null);

  const sortedSignatures = (signatures ?? []).sort((a, b) =>
    a.name.toLowerCase() > b.name.toLowerCase() ? 1 : b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 0
  );

  const options: AllOptions[] = useMemo(
    () => [
      ...sortedSignatures
        .filter(
          (s) => !s.hidden && s.enabled && s.id !== "sphirewall.application.alwaysdeny" && s.id !== "sphirewall.application.alwaysallow"
        )
        .map((s) => convertSignature(s, t)),
    ],
    [sortedSignatures, t]
  );

  const handleChangeSignature = (selected: AllOptions | null) => {
    if (selected) {
      setSelectedSignature(selected);
    } else {
      setSelectedSignature(null);
    }
    onChangeSignatureSearch(selected);
  };

  const handleChangeLocked = (selected: Option | null) => {
    if (selected) {
      setLocked(selected);
      onChangeLocked(selected);
    } else {
      setLocked(null);
      onChangeLocked(null);
    }
  };

  const handleCreatePolicy = (e: React.SyntheticEvent) => {
    onClickCreatePolicy(e);
  };

  const handleTestPolicy = (e: React.SyntheticEvent) => {
    onClickTestPolicy(e);
  };

  const handleNavigateObjectLists = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    context.router.push("/config/device/objects/pools");
  };

  return (
    <Box>
      <Flex flexDirection={"row"} alignItems="end" justifyContent={"end"} paddingEnd={"sp24"}>
        <AuthorizationWrapper allowedRoles={["owner", "surfwize_settings"]}>
          {showObjectLists && !loading && (
            <Button
              variant="outline"
              leftIcon={<Icon icon="fa-bar-chart" boxSize="12px" />}
              marginStart="sp12"
              onClick={handleNavigateObjectLists}
              disabled={disabled}
            >
              {objectLists}
            </Button>
          )}
        </AuthorizationWrapper>
        {showCategorySearch && !loading && (
          <Button
            marginStart="sp12"
            variant="outline"
            leftIcon={<Icon icon="fa-search" boxSize="12px" />}
            onClick={onClickURLSearch}
            disabled={disabled}
          >
            {searchText}
          </Button>
        )}
        {showTestPolicy && !loading && (
          <Tooltip placement="bottom" label="Check website access for a specific user based on your active policy set." variant="dark">
            <Button
              marginStart="sp12"
              variant="outline"
              leftIcon={<Icon icon="fa-sliders" boxSize="12px" />}
              onClick={handleTestPolicy}
              disabled={disabled}
            >
              {testPolicyText}
            </Button>
          </Tooltip>
        )}
      </Flex>

      <Flex width="100%" p="sp24">
        <Box backgroundColor="neutrals.0" borderRadius="6px" width="100%" pl="sp12" pr="sp12" pt="sp12">
          <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Flex flexGrow={1} maxWidth={"80%"} justifyContent="start" alignItems="flex-start" flexWrap="nowrap">
              <Flex maxWidth={536} width="100%" pl="sp12">
                <SearchBox
                  name="searchbox"
                  placeholder={searchBoxText}
                  value={search}
                  onChange={onChangeSearch}
                  onClear={onClearSearch}
                  width="450px"
                />
              </Flex>
              {showFilter && (
                <>
                  <Box pl="sp12" width="100%" maxWidth={"300px"}>
                    <SingleHierarchySelector
                      onChange={(selected: AllOptions | null) => handleChangeSignature(selected)}
                      clearInput={true}
                      options={options}
                      selected={selectedSignature}
                      name="category"
                      id="category"
                      placeholder="Filter by Category or Signature"
                      disabled={loading || !signatures || signatures.length === 0}
                      autoComplete="off"
                    />
                  </Box>
                  <Flex pl="sp12">
                    <SingleSearchSelector
                      options={lockedOptions}
                      onChange={handleChangeLocked}
                      selected={locked}
                      placeholder={t("Filter by Locked/Unlocked")}
                    />
                  </Flex>
                </>
              )}
            </Flex>
            <Flex justifyContent="end" alignItems="center" flexWrap="wrap">
              {disabled && (
                <Box p="sp4">
                  <Spinner data-testid="loading-spinner" />
                </Box>
              )}
              {!loading && (
                <Box p="sp12">
                  <Button
                    variant="primary"
                    leftIcon={<Icon icon="fa-plus" boxSize="18px" />}
                    onClick={handleCreatePolicy}
                    disabled={disabled}
                  >
                    {createPolicyText}
                  </Button>
                </Box>
              )}
            </Flex>
          </Flex>
          <Box px="sp12" pt="sp12" pb="sp24">
            {children}
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default PolicyLayout;

PolicyLayout.contextTypes = {
  router: PropTypes.object.isRequired,
};
